import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axiosDefault from 'axios';
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  TextField,
  PrimaryButton,
  DefaultButton,
  IconButton,
  MessageBar,
  MessageBarType,
  Spinner,
  TooltipHost,
} from '@fluentui/react';

export default function ProposalBioVariationsEditor({
  proposalId,
  axiosInstance,
}) {
  const axios = axiosInstance || axiosDefault;

  // rows: [{ id | null, proposal_id, person_id, full_name, title, email, bio_text, _dirty, _saving, _original }]
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const load = useCallback(async () => {
    if (!proposalId) return;
    setLoading(true);
    setError(null);
    setInfo(null);

    try {
      const { data } = await axios.get(`/api/proposals/${proposalId}/bio_variations`);

      // Expected shape:
      // data = {
      //   staff: [{ person_id, full_name, title, email }, ...],
      //   bios:  [{ id, proposal_id, person_id, bio_text }, ...]
      // }
      const staff = Array.isArray(data?.staff) ? data.staff : [];
      const bios  = Array.isArray(data?.bios)
        ? data.bios
        : (Array.isArray(data) ? data : []);

      // Index bios by person_id
      const biosByPerson = new Map();
      bios.forEach((b) => {
        biosByPerson.set(b.person_id, b);
      });

      // Create one row per staff member, with optional bio attached
      const mergedRows = staff.map((s) => {
        const bio = biosByPerson.get(s.person_id);
        const bio_text = bio?.bio_text || '';
        return {
          id: bio?.id ?? null,
          proposal_id: proposalId,
          person_id: s.person_id,
          name: s.name,
          title: s.title,
          bio_text,
          _dirty: false,
          _saving: false,
          _original: bio_text,
        };
      });

      setRows(mergedRows);
    } catch (e) {
      setError(`Failed to load bios: ${e?.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [axios, proposalId]);

  useEffect(() => {
    load();
  }, [load]);

  const markDirty = useCallback((person_id, bio_text) => {
    setRows((prev) =>
      prev.map((r) =>
        r.person_id === person_id
          ? { ...r, bio_text, _dirty: bio_text !== r._original }
          : r
      )
    );
  }, []);

  const saveOne = useCallback(
    async (row) => {
      const { person_id } = row;
      setInfo(null);
      setError(null);

      // mark saving
      setRows((prev) =>
        prev.map((r) =>
          r.person_id === person_id ? { ...r, _saving: true } : r
        )
      );

      try {
        let saved;
        if (row.id) {
          // Update existing bio
          const { data } = await axios.put('/api/proposals/bio_variations', {
            id: row.id,
            proposal_id: proposalId,
            person_id,
            bio_text: row.bio_text || '',
          });
          saved = data;
        } else {
          // Create new bio for this person
          const { data } = await axios.post('/api/proposals/bio_variations', {
            proposal_id: proposalId,
            person_id,
            bio_text: row.bio_text || '',
          });
          saved = data;
        }

        const newBioText = row.bio_text || '';

        setRows((prev) =>
          prev.map((r) =>
            r.person_id === person_id
              ? {
                  ...r,
                  id: saved?.id ?? r.id, // ensure we capture new ID
                  bio_text: newBioText,
                  _original: newBioText,
                  _dirty: false,
                  _saving: false,
                }
              : r
          )
        );

        setInfo(`Saved bio for ${row.full_name || person_id}.`);
      } catch (e) {
        setRows((prev) =>
          prev.map((r) =>
            r.person_id === person_id ? { ...r, _saving: false } : r
          )
        );
        setError(
          `Failed to save for ${row.full_name || person_id}: ${
            e?.response?.data?.message || e.message
          }`
        );
      }
    },
    [axios, proposalId]
  );

  const saveAll = useCallback(async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (!dirty.length) return;

    setSavingAll(true);
    setInfo(null);
    setError(null);

    try {
      await Promise.all(
        dirty.map((r) => {
          if (r.id) {
            // update
            return axios.put('/api/proposals/bio_variations', {
              id: r.id,
              proposal_id: proposalId,
              person_id: r.person_id,
              bio_text: r.bio_text || '',
            });
          }
          // create
          return axios.post('/api/proposals/bio_variations', {
            proposal_id: proposalId,
            person_id: r.person_id,
            bio_text: r.bio_text || '',
          });
        })
      );

      setRows((prev) =>
        prev.map((r) =>
          r._dirty
            ? { ...r, _dirty: false, _original: r.bio_text }
            : r
        )
      );
      setInfo(`Saved ${dirty.length} bio${dirty.length > 1 ? 's' : ''}.`);
    } catch (e) {
      setError(`Failed to save all: ${e?.response?.data?.message || e.message}`);
    } finally {
      setSavingAll(false);
    }
  }, [axios, proposalId, rows]);

  const columns = useMemo(
    () => [
      {
        key: 'person',
        name: 'Person',
        minWidth: 220,
        onRender: (item) => (
          <Stack>
            <Text variant="mediumPlus">{item.name}</Text>
            <Text
              variant="small"
              styles={{ root: { opacity: 0.7 } }}
            >
              {item.title || '—'} {item.email ? `• ${item.email}` : ''}
            </Text>
          </Stack>
        ),
      },
      {
        key: 'bio',
        name: 'Bio Text',
        minWidth: 500,
        onRender: (item) => (
          <TextField
            multiline
            rows={8}
            placeholder="Write a new bio for this person…"
            value={item.bio_text || ''}
            onChange={(_, v) => markDirty(item.person_id, v || '')}
          />
        ),
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 140,
        maxWidth: 180,
        onRender: (item) => (
          <Stack horizontal tokens={{ childrenGap: 6 }}>
            <TooltipHost
              content={
                item._dirty
                  ? item.id
                    ? 'Save changes'
                    : 'Create new bio'
                  : 'No changes'
              }
            >
              <PrimaryButton
                text={item.id ? 'Save' : 'Create'}
                onClick={() => saveOne(item)}
                disabled={!item._dirty || item._saving}
              />
            </TooltipHost>
            <TooltipHost content="Revert changes">
              <IconButton
                iconProps={{ iconName: 'Undo' }}
                disabled={!item._dirty || item._saving}
                onClick={() =>
                  setRows((prev) =>
                    prev.map((r) =>
                      r.person_id === item.person_id
                        ? {
                            ...r,
                            bio_text: r._original,
                            _dirty: false,
                          }
                        : r
                    )
                  )
                }
              />
            </TooltipHost>
          </Stack>
        ),
      },
    ],
    [markDirty, saveOne]
  );

  if (loading) {
    return (
      <Stack
        verticalAlign="center"
        horizontalAlign="center"
        styles={{ root: { height: '40vh' } }}
      >
        <Spinner label="Loading bio variations…" />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
      >
        <Text variant="xLarge">Proposal Bio Variations</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton
            text={savingAll ? 'Saving…' : 'Save All'}
            onClick={saveAll}
            disabled={savingAll || !rows.some((r) => r._dirty)}
          />
          <DefaultButton text="Reload" onClick={load} />
        </Stack>
      </Stack>

      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setError(null)}
        >
          {error}
        </MessageBar>
      )}
      {info && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setInfo(null)}
        >
          {info}
        </MessageBar>
      )}

      <DetailsList
        items={rows}
        columns={columns}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.justified}
        compact
      />
    </Stack>
  );
}
